import { Button } from "@/components/ui/baseUi/button"
import { Checkbox } from "@/components/ui/baseUi/checkbox"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/baseUi/command"
import { Field } from "@/components/ui/baseUi/field"
import { Input } from "@/components/ui/baseUi/input"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/baseUi/input-group"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/baseUi/popover"
import { Select } from "@/components/ui/baseUi/select"
import { Switch, type SwitchProps } from "@/components/ui/baseUi/switch"
import { Text } from "@/components/ui/baseUi/text"
import { Textarea } from "@/components/ui/baseUi/textarea"
import type { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { getFirstErrorMessage } from "@/components/ui/forms/helpers/formErrorHelpers"
import { useFieldContext } from "@/components/ui/forms/hooks/useFormHook"
import { PhoneNumberInput } from "@/components/ui/forms/phoneNumberInput"
import { SearchUserModal, type SearchUserModalRef } from "@/components/ui/forms/searchUserInput"
import { UserCellWithTooltip } from "@/components/ui/layout/table/userCell"
import { PasswordInput } from "@/modules/auth/components/passwordInput"
import { type MediaItemFormType } from "@/modules/common/media/mediaFormTypes"
import { useCountrySubdivisionsListArray } from "@/modules/language/utils/countries"
import { cn } from "@/modules/lib/utils"
import { HUGalleryForm } from "@/modules/ui/components/huGalleryForm"
import { HUImageForm } from "@/modules/ui/components/huImageForm"
import { word } from "@modules/core/utils/i18n"
import { getCurrencyPosition, getCurrencySymbol } from "@modules/currency/currency"
import type { ExploreProfileItemEntity } from "@modules/explore/entities/exploreEntity"
import type { UserEntity } from "@modules/profile/userProfile/userEntity"
import { type TCountryCode } from "countries-list"
import { CheckIcon, ChevronDown, ChevronUp, ChevronsUpDownIcon, PencilIcon, Search, XIcon } from "lucide-react"
import { useEffect, useEffectEvent, useMemo, useRef, useState } from "react"

function FormInputGroupInput(props: React.ComponentProps<typeof InputGroupInput>) {
  const field = useFieldContext<string>()
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
  return (
    <InputGroupInput
      id={field.name}
      name={field.name}
      value={field.state.value}
      placeholder={props.placeholder}
      onBlur={field.handleBlur}
      onChange={(e) => {
        field.handleChange(e.target.value)
      }}
      aria-invalid={isInvalid}
      {...props}
    />
  )
}

function FormInput(props: React.ComponentProps<typeof Input>) {
  const field = useFieldContext<string>()
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
  return (
    <Input
      id={field.name}
      name={field.name}
      value={field.state.value}
      placeholder={props.placeholder}
      onBlur={field.handleBlur}
      onChange={(e) => {
        field.handleChange(e.target.value)
      }}
      aria-invalid={isInvalid}
      {...props}
    />
  )
}

function FormPassword({ inputProps, ...props }: React.ComponentProps<typeof PasswordInput>) {
  const field = useFieldContext<string>()
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
  return (
    <PasswordInput
      id={field.name}
      inputProps={{
        ...inputProps,
        name: field.name,
        value: field.state.value,
        onChange: (e) => {
          field.handleChange(e.target.value)
        },
        "aria-invalid": isInvalid,
      }}
      onBlur={field.handleBlur}
      {...props}
    />
  )
}

/* Not completed yet */
function FormSelect(props: React.ComponentProps<typeof Select>) {
  const field = useFieldContext<string>()
  return (
    <Select
      name={field.name}
      value={field.state.value}
      onValueChange={(value) => {
        field.handleChange(String(value))
      }}
      {...props}
    />
  )
}

function FormTextarea(props: React.ComponentProps<typeof Textarea>) {
  const field = useFieldContext<string>()
  return (
    <Textarea
      id={field.name}
      name={field.name}
      value={field.state.value}
      placeholder={props.placeholder}
      onBlur={field.handleBlur}
      onChange={(e) => {
        field.handleChange(e.target.value)
      }}
      {...props}
    />
  )
}

/* Not completed yet */
function FormCheckbox(props: React.ComponentProps<typeof Checkbox>) {
  const field = useFieldContext<boolean>()
  return (
    <Checkbox
      id={field.name}
      name={field.name}
      checked={field.state.value}
      onCheckedChange={(checked) => {
        field.handleChange(checked)
      }}
      {...props}
    />
  )
}

/* Not completed yet */
function FormSwitch(props: React.ComponentProps<typeof Switch>) {
  const field = useFieldContext<boolean>()
  return (
    <Switch
      id={field.name}
      name={field.name}
      checked={field.state.value}
      onCheckedChange={(checked) => {
        field.handleChange(checked)
      }}
      {...props}
    />
  )
}

type FormSwitchInlineProps = {
  textSwitch: string
  switchProps?: SwitchProps
}

function FormSwitchInline({ textSwitch, switchProps }: FormSwitchInlineProps) {
  const field = useFieldContext<boolean>()
  return (
    <div className="flex w-full flex-row items-center justify-between rounded-2xl bg-surface-foreground px-5 py-3.5">
      <Text fontStyle="BM">{textSwitch}</Text>
      <Switch
        id={field.name}
        checked={field.state.value}
        onCheckedChange={(checked) => {
          field.handleChange(checked)
        }}
        {...switchProps}
      />
    </div>
  )
}

function FormNumberInput(props: Omit<React.ComponentProps<typeof Input>, "type" | "value" | "onChange" | "onBlur">) {
  const field = useFieldContext<number | null>()
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid
  return (
    <Input
      id={field.name}
      name={field.name}
      type="number"
      inputMode="numeric"
      value={field.state.value ?? ""}
      onBlur={field.handleBlur}
      onChange={(e) => {
        field.handleChange(e.target.value === "" ? null : Number(e.target.value))
      }}
      aria-invalid={isInvalid}
      {...props}
    />
  )
}

type FormPriceInputProps = {
  currency?: string
  locale: string
  inputProps?: React.ComponentProps<"input">
}

function FormPriceInput({ currency, locale, inputProps }: FormPriceInputProps) {
  const field = useFieldContext<number | null>()

  const currencyPosition = getCurrencyPosition(locale, currency ?? "EUR")
  const currencySymbol = getCurrencySymbol(currency ?? "EUR", locale)

  return (
    <InputGroup>
      {currency && currencyPosition === "left" && (
        <InputGroupAddon align="inline-start">{currencySymbol}</InputGroupAddon>
      )}
      <InputGroupInput
        type="number"
        inputMode="decimal"
        step={0.1}
        min={0}
        id={field.name}
        value={field.state.value ?? ""}
        onChange={(e) => {
          const next = e.target.value === "" ? null : Number(e.target.value)
          field.handleChange(next)
        }}
        onBlur={field.handleBlur}
        {...inputProps}
      />
      {currency && currencyPosition === "right" && (
        <InputGroupAddon align="inline-end">{currencySymbol}</InputGroupAddon>
      )}
    </InputGroup>
  )
}

function FieldField(props: React.ComponentProps<typeof Field>) {
  const field = useFieldContext<string>()
  const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid

  return <Field {...props} data-invalid={isInvalid} />
}

type FieldMessageProps = React.ComponentProps<"p"> & { className?: string }

function FieldMessage({ className, ...props }: FieldMessageProps) {
  const field = useFieldContext<string>()
  const errorMessage = getFirstErrorMessage(field.state.meta.errors)

  if (errorMessage && field.state.meta.isTouched) {
    return (
      <p className={cn("label-small text-textError", className)} {...props}>
        {errorMessage}
      </p>
    )
  }
  return null
}

// ── FormPhoneInput ────────────────────────────────────────────────────────────
// Field value: { number: string; countryCode: string }
function FormPhoneInput(
  props: Omit<
    React.ComponentProps<typeof PhoneNumberInput<string, string>>,
    "name" | "countryCodeName" | "getFieldProps" | "setFieldValue" | "countryCode"
  >,
) {
  const field = useFieldContext<{ number: string; countryCode: string }>()
  return (
    <PhoneNumberInput
      name="number"
      countryCodeName="countryCode"
      countryCode={field.state.value.countryCode as TCountryCode | undefined}
      getFieldProps={(name) => ({
        name: String(name),
        value: String(name) === "number" ? field.state.value.number : field.state.value.countryCode,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          if (String(name) === "number") {
            field.handleChange({ ...field.state.value, number: e.target.value })
          }
        },
        onBlur: field.handleBlur,
      })}
      setFieldValue={async (name, value) => {
        if (name === "countryCode") {
          field.handleChange({ ...field.state.value, countryCode: String(value) })
        } else if (name === "number") {
          field.handleChange({ ...field.state.value, number: String(value) })
        }
        return Promise.resolve()
      }}
      {...props}
    />
  )
}

// ── FormCountrySelect ─────────────────────────────────────────────────────────
// Field value: string (ISO country code, e.g. "FR")
function FormCountrySelect({ className }: { className?: string }) {
  const field = useFieldContext<string>()
  const [open, setOpen] = useState(false)
  const countryOptions = useCountrySubdivisionsListArray()
  const selectedLabel = useMemo(
    () => countryOptions.find((c) => c.code === field.state.value)?.label,
    [countryOptions, field.state.value],
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "h-11 w-full justify-between bg-transparent font-normal hover:border-interactive-border-primary-default hover:bg-transparent",
              className,
            )}
          >
            {selectedLabel ?? word("coach.admin.vitrine.header.country")}
            <ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        }
      />
      <PopoverContent className="w-65 border-0 p-0" align="start">
        <Command>
          <CommandInput placeholder={word("global.searchCountry")} />
          <CommandList>
            <CommandEmpty>{word("global.searchCountryNotFound")}</CommandEmpty>
            <CommandGroup>
              {countryOptions.map((c) => (
                <CommandItem
                  key={c.code}
                  value={c.label}
                  onSelect={() => {
                    field.handleChange(c.code)
                    setOpen(false)
                  }}
                  className="cursor-pointer"
                >
                  <CheckIcon
                    className={cn("mr-2 size-4", field.state.value === c.code ? "opacity-100" : "opacity-0")}
                  />
                  {c.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

// ── FormImagePicker ───────────────────────────────────────────────────────────
// Field value: MediaItemFormikType | null
function FormImagePicker(props: Omit<React.ComponentProps<typeof HUImageForm>, "image" | "setImage">) {
  const field = useFieldContext<MediaItemFormType | null>()
  return (
    <HUImageForm
      image={field.state.value}
      setImage={(media?: MediaItemFormType) => {
        field.handleChange(media ?? null)
      }}
      {...props}
    />
  )
}

// ── FormGalleryPicker ─────────────────────────────────────────────────────────
// Field value: MediaItemFormikType[]
function FormGalleryPicker(props: Omit<React.ComponentProps<typeof HUGalleryForm>, "images" | "onChange">) {
  const field = useFieldContext<MediaItemFormType[]>()
  return (
    <HUGalleryForm
      images={field.state.value}
      onChange={(images) => {
        field.handleChange(images)
      }}
      {...props}
    />
  )
}

// ── FormNumberCursor ──────────────────────────────────────────────────────────
// Field value: number
type FormNumberCursorProps = {
  min?: number
  max?: number
} & React.ComponentProps<"div">

function FormNumberCursor({ min, max, className, ...props }: FormNumberCursorProps) {
  const field = useFieldContext<number>()

  const increment = () => {
    if (max !== undefined && field.state.value >= max) return
    field.handleChange(field.state.value + 1)
  }

  const decrement = () => {
    if (min !== undefined && field.state.value <= min) return
    field.handleChange(field.state.value - 1)
  }

  return (
    <div
      className={cn("flex justify-between gap-3 rounded-full border border-borderDefault px-3", className)}
      {...props}
    >
      <span className="flex items-center">{field.state.value}</span>
      <div className="flex flex-col">
        <ChevronUp size={20} onClick={increment} className="cursor-pointer" />
        <ChevronDown size={20} onClick={decrement} className="cursor-pointer" />
      </div>
    </div>
  )
}

// ── FormSearchUser ────────────────────────────────────────────────────────────
// Field value: string | null (the selected user id)
type FormSearchUserProps = {
  buttonTextSearch?: string
  searchDescriptionText?: string
  user?: UserEntity | null
  className?: string
  tooltipProps?: Omit<React.ComponentProps<typeof Tooltip>, "children">
  tooltipTriggerProps?: Omit<React.ComponentProps<typeof TooltipTrigger>, "children" | "asChild">
  tooltipContentProps?: Omit<React.ComponentProps<typeof TooltipContent>, "children">
} & React.ComponentProps<"div">

function FormSearchUser({
  buttonTextSearch,
  searchDescriptionText,
  user,
  className,
  tooltipProps,
  tooltipTriggerProps,
  tooltipContentProps,
}: FormSearchUserProps) {
  const field = useFieldContext<string | null>()
  const searchUserModal = useRef<SearchUserModalRef>(null)
  const [userSelected, setUserSelected] = useState<ExploreProfileItemEntity | UserEntity | null>(user ?? null)

  // Clear the local display when the field value is reset externally (e.g. form reset).
  const resetUserSelected = useEffectEvent(() => {
    setUserSelected(null)
  })

  useEffect(() => {
    if (field.state.value === null && userSelected !== null) {
      resetUserSelected()
    }
  }, [field.state.value, userSelected])

  const handlePick = (profile: ExploreProfileItemEntity) => {
    setUserSelected(profile)
    field.handleChange(profile.id)
  }

  const handleTrash = () => {
    setUserSelected(null)
    field.handleChange(null)
  }

  return (
    <>
      <div className={cn("flex flex-col", className)}>
        {userSelected ? (
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 flex-1 overflow-hidden">
              <UserCellWithTooltip
                tooltipProps={tooltipProps}
                tooltipTriggerProps={tooltipTriggerProps}
                tooltipContentProps={tooltipContentProps}
                member={{
                  avatar: userSelected.avatar ?? undefined,
                  firstname: userSelected.firstname,
                  lastname: userSelected.lastname,
                }}
                subtitle={userSelected.username === "" ? "" : "@" + userSelected.username}
              />
            </div>
            <div className="flex shrink-0 items-center justify-center gap-2">
              <Button variant="secondary" size="iconMd" onClick={() => searchUserModal.current?.open()}>
                <PencilIcon />
              </Button>
              <Button variant="secondary" size="iconMd" onClick={handleTrash}>
                <XIcon />
              </Button>
            </div>
          </div>
        ) : (
          <Button
            variant="outline"
            size="lg"
            className="grow justify-start text-textDefault-placeholder"
            onClick={() => searchUserModal.current?.open()}
          >
            <Search size={16} color="currentColor" className="text-textDefault-body" />
            {buttonTextSearch ?? word("searchUserInput.modal.searchText")}
          </Button>
        )}
      </div>
      <SearchUserModal
        ref={searchUserModal}
        setUserSelected={handlePick}
        searchDescriptionText={searchDescriptionText}
      />
    </>
  )
}

export {
  FieldField,
  FieldMessage,
  FormCheckbox,
  FormCountrySelect,
  FormGalleryPicker,
  FormImagePicker,
  FormInput,
  FormInputGroupInput,
  FormNumberCursor,
  FormNumberInput,
  FormPassword,
  FormPhoneInput,
  FormPriceInput,
  FormSearchUser,
  FormSelect,
  FormSwitch,
  FormSwitchInline,
  FormTextarea,
}
